/**
 * First-game coaching: one short tip per situation, shown once. Seen tips
 * are remembered on the device; the Rules panel always has the full story.
 */
import type { GameState, PlayerId } from '@horde/engine';
import { isPinned, ownedBy } from '@horde/engine';

const KEY = 'horde:tips:v1';

export type TipId =
  | 'reinforce'
  | 'draw'
  | 'attack'
  | 'pinned'
  | 'purge'
  | 'occupy'
  | 'fortify'
  | 'rising'
  | 'hold-card'
  | 'objective';

const TEXT: Record<TipId, string> = {
  reinforce:
    'Start of your turn: place your new units. Border territories and anything with zombies inside need them most.',
  draw: 'Every turn you must draw a card. Some help, some hurt, and the effects grow as the rounds go on.',
  attack:
    'Tap one of your territories, then a neighbour. Three dice against two; ties go to the defender.',
  pinned:
    'A territory with as many zombies as units is pinned: it cannot attack out. Tap it twice to purge first.',
  purge: 'Purging fights the zombies inside your own territory. Every unit there gets to roll.',
  occupy:
    'You took it. Move in at least as many units as dice you rolled; more if you want to hold it.',
  fortify:
    'Last step: move units once along your own territories, or just end the turn. Then the dead rise.',
  rising: 'Zombie Rising: your territories rolled for spawns and bites. Watch the green numbers.',
  'hold-card':
    'That card stays in your hand. Play it from the right-hand panel on any of your turns, then pick a target.',
  objective:
    'Win by holding half the world as the last faction standing, or wipe out every zombie if Eradication is on.',
};

let seen: Set<TipId> = (() => {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) ?? '[]') as TipId[]);
  } catch {
    return new Set();
  }
})();

export function tipText(id: TipId): string {
  return TEXT[id];
}

export function markSeen(id: TipId): void {
  seen.add(id);
  try {
    localStorage.setItem(KEY, JSON.stringify([...seen]));
  } catch {
    // ignore
  }
}

export function resetTips(): void {
  seen = new Set();
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/** The single most relevant unseen tip for the current situation, if any. */
export function pickTip(
  state: GameState,
  me: PlayerId,
  myTurn: boolean,
  hasHoldCard: boolean,
  risingJustHappened: boolean,
): TipId | null {
  const candidates: TipId[] = [];
  if (risingJustHappened) candidates.push('rising');
  if (myTurn) {
    if (state.pendingOccupy) candidates.push('occupy');
    else if (state.phase === 'reinforce') candidates.push('reinforce', 'objective');
    else if (state.phase === 'draw') candidates.push('draw');
    else if (state.phase === 'attack') {
      const mine = ownedBy(state, me);
      if (mine.some((id) => isPinned(state, id))) candidates.push('pinned');
      if (mine.some((id) => state.territories[id]!.zombies > 0)) candidates.push('purge');
      candidates.push('attack');
    } else if (state.phase === 'fortify') candidates.push('fortify');
    if (hasHoldCard) candidates.push('hold-card');
  }
  return candidates.find((c) => !seen.has(c)) ?? null;
}
